import React from "react";
import { createPortal } from "../windowManager/browserFenster/fensterPortal";
import { useTelephony } from "../../context/TelephonyContext";
import { useElapsed, fmtMMSS } from "./callTimer";
import { anrufAnzeigeName, anrufAnzeigeNummer } from "./telephonyUtils";
// Global (über dem Provider) gemountet → kvp-rd-Styles explizit laden, damit die
// minimierte Leiste auf jeder Seite korrekt aussieht (vgl. CallWidget).
import "../redesign/contactRedesign.css";

/**
 * ActiveCallBar — schwebende, kompakte Statusleiste des minimierten Anrufs
 * (SP2 → P5: Top-Layout-Leiste → schwebende Bottom-Leiste → breite Pille OBEN-zentriert).
 *
 * Sichtbar NUR, wenn ein Anruf läuft UND minimiert ist. Wird global (per Portal
 * nach document.body) gerendert — NICHT im NavigationLayout-Fluss — und schwebt als
 * breite, abgerundete Pille oben-zentriert über dem Inhalt (position:fixed, s.
 * `.callbar` in contactRedesign.css). Zeigt Name/Nummer + Timer/Status + Stumm +
 * Beenden + „Öffnen" (restore). Blur-frei, kvp-rd-Tokens. Läuft das Gespräch am
 * Tischtelefon (ADR 0001, Nachtrag), fehlen Stumm/Beenden — dafür steht dort
 * „Tischtelefon".
 */

const ico = (d: React.ReactNode): React.ReactElement => (
    <svg width={16} height={16} viewBox="0 0 20 20" fill="none" stroke="currentColor"
        strokeWidth={1.7} strokeLinecap="round" strokeLinejoin="round">
        {d}
    </svg>
);
const MicIcon = ico(<><rect x="7.5" y="2.5" width="5" height="9" rx="2.5" /><path d="M5 9.5a5 5 0 0010 0M10 14.5v3M7.5 17.5h5" /></>);
const MicOffIcon = ico(<><path d="M7.5 4.2A2.5 2.5 0 0112.5 5v3M12.5 9.6a2.5 2.5 0 01-3.6 1.9" /><path d="M5 9.5a5 5 0 007.5 4.3M14.8 9.5a5 5 0 01-1 3M10 16v1.5M7.5 17.5h5M3.5 3.5l13 13" /></>);
const HangupIcon = ico(
    <><path d="M3.2 8.5c4-2.8 9.6-2.8 13.6 0l-1.1 2.3-2.7-.4-.5-1.7a7.6 7.6 0 00-4.2 0l-.5 1.7-2.7.4z" />
        <path d="M14.5 12.5l2.5 3M17 12.5l-2.5 3" /></>,
);
const ExpandIcon = ico(<path d="M6 12l4-4 4 4" />);

const ActiveCallBar: React.FC = () => {
    const { activeCall, owners, minimized, toggleMute, hangup, restore, notizFuer } = useTelephony();
    const elapsed = useElapsed(activeCall ? activeCall.startedAt : null);

    if (!activeCall || !minimized) return null;

    // Anzeigename: KMVS-Kontakt vor dem Telefonbuch der Anlage (s. anrufAnzeigeName).
    const namensQuelle = {
        number: activeCall.number,
        pbxName: activeCall.name,
        kontaktName: owners?.displayName ?? null,
    };
    const displayName = anrufAnzeigeName(namensQuelle);
    // Nummer als Unterzeile nur, wenn darüber ein Name steht.
    const sub = anrufAnzeigeNummer(namensQuelle);
    const showTimer = activeCall.state === "active";

    return createPortal(
        <div className="kvp-rd callbar" role="status" aria-label="Aktiver Anruf (minimiert)">
            <i className="callbar-dot" aria-hidden />
            <div className="callbar-id">
                <span className="callbar-name" title={displayName}>{displayName}</span>
                {sub && <span className="callbar-sub">{sub}</span>}
            </div>
            {showTimer ? (
                <span className="callbar-timer">{fmtMMSS(elapsed)}</span>
            ) : (
                <span className="callbar-timer">
                    {activeCall.state === "ringing" ? "klingelt" : "wählt …"}
                </span>
            )}
            {activeCall.amTischtelefon && (
                <span className="callbar-sub" title="Gespräch läuft am Tischtelefon oder in der Linkus-App">
                    Tischtelefon
                </span>
            )}
            {notizFuer(activeCall.callId).trim() && (
                <span className="callbar-sub" title="Notiz zum Gespräch vorhanden — „Öffnen“ zum Weiterschreiben">
                    Notiz
                </span>
            )}
            {!activeCall.amTischtelefon && (
                <>
                    <button
                        className={`callbar-btn icon${activeCall.isMute ? " on" : ""}`}
                        onClick={toggleMute}
                        type="button"
                        aria-pressed={activeCall.isMute}
                        title={activeCall.isMute ? "Laut schalten" : "Stummschalten"}
                    >
                        {activeCall.isMute ? MicOffIcon : MicIcon}
                    </button>
                    <button className="callbar-btn icon danger" onClick={hangup} type="button" title="Beenden">
                        {HangupIcon}
                    </button>
                </>
            )}
            <button className="callbar-btn" onClick={restore} type="button" title="Aufklappen">
                {ExpandIcon} Öffnen
            </button>
        </div>,
        document.body,
    );
};

export default ActiveCallBar;
